import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageLoader } from "@/components/PageLoader";

import { useUserOrganizations } from "@/hooks/useUserOrganizations";
import { ROUTES } from "@/routes/paths";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PageError } from "@/components/PageError";
import { useDebug } from "@/hooks/useDebug";
import { useHotjarIdentify } from "@/hooks/useHotjarIdentify";

export const OrganizationRoute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    data: groupOrgsData,
    loading: orgsLoading,
    loadingProgress,
    error: orgsError,
  } = useUserOrganizations();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { orgId } = useParams<{ orgId: string | undefined }>();
  const org = groupOrgsData?.find((org) => org.id === orgId);

  const { loading: permissionsLoading, error: permissionsError } =
    useOrgPermissions();

  // Identify user with Hotjar (hook handles its own loading checks internally)
  useHotjarIdentify();

  useEffect(() => {
    if (!orgsLoading && !org && groupOrgsData) {
      // redirect to primary org if :orgId param is missing or invalid
      navigate(
        ROUTES.ORGANIZATION.replace(":orgId", groupOrgsData[0].id || ""),
      );
    }
  }, [org, groupOrgsData, navigate, orgsLoading]);

  const { error } = useDebug();

  useEffect(() => {
    if (orgsError) toast.error(t("error.organization.loading"));
    if (permissionsError) toast.error(t("error.organization.permissions"));
  }, [orgsError, permissionsError, t]);

  if (orgsLoading || permissionsLoading) {
    return <PageLoader progress={orgsLoading ? loadingProgress : undefined} />;
  }

  if (permissionsError || orgsError) {
    error("OrganizationRoute: errors", permissionsError, orgsError);
    return <PageError />;
  }

  return children;
};
